import { useMemo } from 'react';

import { getUserTimezone } from '@occc-app/lib/date';

import { RetailerNetworkLoyaltyPerformance } from './retailer-network-loyalty-performance.component';
import {
  getRetailConnectLoyaltyPerformanceMetrics,
  getRetailConnectLoyaltyPerformanceTimeSeries,
} from './retailer-network-loyalty-performance.tools';

import { useGetRetailerNetworkLoyaltyMetricsOverviewQuery } from '../../../data-access/queries';
import { RetailerNetworkDataLoading } from '../../components/retailer-network-data-loading.component';

type RetailerNetworkLoyaltyPerformanceContainerProps = {
  retailerNetworkUuid: string;
  startDate: Date;
  endDate: Date;
  periodLabel: string;
};

export const RetailerNetworkLoyaltyPerformanceContainer = ({
  retailerNetworkUuid,
  startDate,
  endDate,
  periodLabel,
}: RetailerNetworkLoyaltyPerformanceContainerProps) => {
  const { data, error, loading } = useGetRetailerNetworkLoyaltyMetricsOverviewQuery({
    variables: {
      filter: {
        retailerNetworkUuidEquals: retailerNetworkUuid,
        timezone: getUserTimezone(),
        dateRange: {
          endDate: endDate.toISOString(),
          startDate: startDate.toISOString(),
        },
      },
    },
    // Apollo incorrectly cached and merged the loyaltyTransactionsTimeSeries array (of type (date: string, value: number))
    // Apollo always returns [{}, {}, ...] list of empty objects
    // The politic TimeSeriesMetric: { keyFields: false } is not helpful.
    fetchPolicy: 'no-cache',
  });

  const metrics = useMemo(
    () =>
      getRetailConnectLoyaltyPerformanceMetrics(
        data?.actualPeriodPerformance,
        data?.prevPeriodPerformance,
        periodLabel,
      ),
    [data?.actualPeriodPerformance, data?.prevPeriodPerformance, periodLabel],
  );

  const series = useMemo(
    () => getRetailConnectLoyaltyPerformanceTimeSeries(data?.loyaltyTransactionsTimeSeries),
    [data?.loyaltyTransactionsTimeSeries],
  );

  return (
    <RetailerNetworkDataLoading isLoading={loading} hasData={!!data} hasError={!!error}>
      <RetailerNetworkLoyaltyPerformance
        totalMetrics={metrics.total}
        loyaltyMetrics={metrics.loyalty}
        salesMetrics={metrics.sales}
        transactionsTimeSeries={series}
      />
    </RetailerNetworkDataLoading>
  );
};
